
interface OneIncNamespace {
  PortalOne: new (container: any, callback: any) => any;
}

declare const OneInc: OneIncNamespace;

import {
  quickpayMapActions,
  quickpayMapMutations,
  quickpayMapState
} from "@/store/modules/quickpay";
import Vue from "vue";
import * as types from "@/store/mutation-types";
import SimpleNote from "./SimpleNote.vue";
import DataView from "@/components/DataView/DataView.vue";
import { get } from "lodash";
import { formatDate } from "@/helpers/dateProcessor";
import { formatCurrencyFloat } from "@/plugins/filters";

import { getActiveSite } from "@/helpers/generalHelpers";

export default Vue.extend({
  components: { SimpleNote, DataView },
  name: "payment-info",
  props: {
    isInUS: {
      type: Boolean,
      default: false
    }
  },
  created() {
    let oneIncPaymentScript = document.createElement("script");
    oneIncPaymentScript.setAttribute("async", "");
    oneIncPaymentScript.setAttribute("defer", "");
    const oneIncBaseURL =
      process.env.VUE_APP_ENV === "production"
        ? "https://portalone.processonepayments.com"
        : "https://stgportalone.processonepayments.com";
    oneIncPaymentScript.setAttribute(
      "src",
      `${oneIncBaseURL}/GenericModalV2/PortalOne.js?purejs`
    );
    document.head.appendChild(oneIncPaymentScript);
  },
  data() {
    return {
      showCaution: false,
      paymentOptions: [
        { label: "E-Check ~ Processing Fee Waived", value: "ECheck" },
        { label: "Credit Card ~ Processing Fee - 2.99%", value: "CreditCard" }
      ],
      error: "",
      errorDetails: "",
      container: {} as HTMLElement,
      processedTransactionIds: new Set<string>(), // Track processed transactions to prevent duplicates
      isProcessingPayment: false // Prevent double-clicking Pay button
    };
  },
  mounted() {
    this.container =
      (this?.$refs?.portalOneContainer as HTMLElement) ||
      (document.getElementById("portalOneContainer") as HTMLElement);
  },
  methods: {
    ...quickpayMapMutations([types.SET_STATE]),
    ...quickpayMapActions(["getSessionId", "saveTransaction"]),
    editField(payload: any): void {
      this.SET_STATE(payload);
    },
    goHome() {
      this.$router.push("/").catch(() => {});
    },
    async pay(): Promise<any> {
      // Prevent double-clicking the Pay button
      if (this.isProcessingPayment) {
        return;
      }

      if (!this.isInUS) {
        return;
      }
      if (this.amountLessThanFive) {
        return this.$appNotify({
          type: "error",
          title: "Current amount due",
          message: "Current amount cannot be less than $5"
        });
      }

      this.isProcessingPayment = true;
      try {
        const site = getActiveSite(/*prefix*/);
        const key = await this.getSessionId(site);
        if (key) {
          this.openModalWindow(key);
          // isProcessingPayment stays true until the portal modal is closed,
          // payment completes, or payment is canceled — those event handlers
          // are responsible for resetting the flag (see subscribeToEvents).
        } else {
          this.$appNotify({
            type: "error",
            title: "Error creating session key",
            message: `The payment portal loading failed.`
          });
          this.isProcessingPayment = false;
        }
      } catch (error) {
        this.$appNotify({
          type: "error",
          title: "Payment Processing Error",
          message: "Unable to initiate payment. Please try again."
        });
        this.isProcessingPayment = false;
      }
    },
    requiredChecks(): boolean {
      const minimumAmountDue = get(this.policyInfo, "currentAmtDue") || 0;
      const totalAmountDue = get(this.policyInfo, "totalAmountDue") || 0;
      let currentAmtDue = get(this.policyInfo, "currentAmtDue") || 0;
      if (currentAmtDue < minimumAmountDue || currentAmtDue > totalAmountDue) {
        this.$appNotifyError(
          "'Amt to Pay' must be equal to or greater than the 'Min Due' and not exceed the 'Pay In Full' amount"
        );

        this.policyInfo.currentAmtDue = minimumAmountDue as any;
        return false;
      }
      return true;
    },
    customFieldValidator() {
      return this.validCurrentAmount;
    },
    subscribeToEvents(container: any): void {
      if (!container) {
        return;
      }
      container.addEventListener("portalOne.load", function() {});
      container.addEventListener("portalOne.unload", () => {
        // Reset processing state when modal closes
        this.isProcessingPayment = false;
        container.removeEventListener("portalOne.error", (e: any) => {
          const { detail: error } = e;
          const payload = this.getTransactionData(error, "failed");
          this.doSaveTransaction(payload);
        });
        container.removeEventListener(
          "portalOne.paymentComplete",
          (event: CustomEvent) => {
            const response = event.detail;
            this.$notify.success({
              title: "",
              message: this.paymentSuccessMsg,
              customClass: "quick-pay-notification"
            });
            if (response.transactions && response.transactions.length) {
              for (let transaction of response.transactions) {
                const payload = this.getTransactionData(
                  transaction,
                  "succeeded"
                );
                this.doSaveTransaction(payload);
              }
            } else {
              const payload = this.getTransactionData(response, "succeeded");
              this.doSaveTransaction(payload);
            }
          }
        );
        this.goHome();
      });
      container.addEventListener("portalOne.error", (e: any) => {
        const { detail: error } = e;
        const payload = this.getTransactionData(error, "failed");
        this.doSaveTransaction(payload);
      });
      container.addEventListener(
        "portalOne.paymentComplete",
        (event: CustomEvent) => {
          const response = event.detail;
          this.$notify.success({
            title: "",
            message: this.paymentSuccessMsg,
            customClass: "quick-pay-notification"
          });
          if (response.transactions && response.transactions.length) {
            for (let transaction of response.transactions) {
              const transactionId = transaction.transactionId;
              if (!this.processedTransactionIds.has(transactionId)) {
                this.processedTransactionIds.add(transactionId);
                const payload = this.getTransactionData(
                  transaction,
                  "succeeded"
                );
                this.doSaveTransaction(payload);
              }
            }
          } else {
            const transactionId = response.transactionId;
            if (!this.processedTransactionIds.has(transactionId)) {
              this.processedTransactionIds.add(transactionId);
              const payload = this.getTransactionData(response, "succeeded");
              this.doSaveTransaction(payload);
            }
          }
        }
      );
      container.addEventListener("portalOne.paymentCanceled", () => {
        // Reset processing state when payment is canceled
        this.isProcessingPayment = false;
      });
      container.addEventListener("portalOne.saveComplete", function() {});
      container.addEventListener("portalOne.saveCanceled", function() {});
    },
    async doSaveTransaction(payload: any) {
      try {
        await this.saveTransaction(payload);
      } catch (error) {
        // Check if this is a duplicate transaction error
        if (this.isDuplicateTransactionError(error)) {
          // Log but don't show error to user - transaction already exists
          console.warn("Transaction already exists:", payload.transactionId);
          return;
        }

        // For other errors, notify Bugsnag and show user error
        this.$bugSnagClient.notify(error);
        this.$notify.error({
          title: "Transaction Save Error",
          message:
            "There was an issue saving your transaction. Please contact support if needed.",
          customClass: "quick-pay-notification"
        });
      }
    },

    /**
     * Check if the error is related to duplicate transaction ID
     */
    isDuplicateTransactionError(error: any): boolean {
      const errorMessage = error?.message || "";
      const responseMessage =
        error?.raw?.response?.data?.response?.message || "";

      // Check for MongoDB duplicate key error pattern for transactionId
      const duplicateKeyPattern = /E11000 duplicate key error.*transactionId.*dup key/i;

      return (
        duplicateKeyPattern.test(errorMessage) ||
        duplicateKeyPattern.test(responseMessage)
      );
    },
    getTransactionData(response: any, status: string) {
      let payload = {};
      switch (status) {
        case "succeeded":
          payload = {
            companyNumber: this.policyInfo.companyNumber,
            transactionPolicyNumber: get(this.policyInfo, "policyNumber"),
            transactionId: response.transactionId,
            customerName: response.customerName,
            transactionDate: response.transactionDate,
            timeZone: response.timeZone,
            sessionId: response.sessionId,
            paymentCategory:
              response.paymentCategory === "CreditCard" ? "card" : "check",
            lastFourDigits: response.lastFourDigits,
            paymentAmount: response.paymentAmount,
            holderZip: response.holderZip,
            totalAmount: response.totalPaymentAmount,
            convenienceFee: response.convenienceFee,
            clientReferenceData: response.clientReferenceData1,
            cardType: response.cardType,
            cardExpirationYear: response.cardExpirationYear,
            cardExpirationMonth: response.cardExpirationMonth,
            batchNumber: response.batchNumber,
            authCode: response.authCode ? response.authCode : "",
            tokenId: response.tokenId,
            policyPrefix: get(this.policyInfo, "policyPrefix"),
            mode: this.mode,
            transactionDescription:
              response.paymentCategory === "CreditCard" ? "Credit Card" : "ACH",
            status
          };
          break;
        case "failed":
          payload = {
            companyNumber: this.policyInfo.companyNumber,
            transactionPolicyNumber: get(this.policyInfo, "policyNumber"),
            policyPrefix: get(this.policyInfo, "policyPrefix"),
            responseMessage: response.Description,
            paymentAmount: get(this.policyInfo, "currentAmtDue"),
            totalAmount: get(this.policyInfo, "totalAmountDue"),
            transactionDate: new Date(),
            customerName: get(this.policyInfo, "name"),
            paymentCategory:
              get(this.policyInfo, "paymentMethod") === "CreditCard"
                ? "card"
                : "check",
            status
          };
          break;
      }
      return payload;
    },
    openModalWindow(sessionKey: string): void {
      if (!this.container) {
        this.container =
          (this?.$refs?.portalOneContainer as HTMLElement) ||
          (document.getElementById("portalOneContainer") as HTMLElement);
      }

      if (!this.container) {
        this.$appNotify({
          type: "error",
          title: "Payment Form Error",
          message:
            "Payment form failed to load. Please reload the page and try again, or call Billing at 888-786-0003, option #2."
        });
        this.isProcessingPayment = false;
        return;
      }

      let portalOne = null;

      if (typeof OneInc !== "undefined" && OneInc.PortalOne) {
        portalOne = new OneInc.PortalOne(
          this.container,
          this.subscribeToEvents
        );
      }

      if (
        portalOne &&
        typeof portalOne.makePayment === "function" &&
        this.policyInfo
      ) {
        portalOne.makePayment({
          paymentCategory: this.policyInfo.paymentMethod,
          feeContext: this.feeContext,
          amountContext: "SelectAmount",
          minAmountDue: this.policyInfo.currentAmtDue,
          accountBalance: this.policyInfo.totalAmountDue,
          billingZip: this.policyInfo.zip.Zip,
          billingAddressStreet: this.policyInfo.printAddress1,
          policyHolderName: this.policyInfo.name,
          clientReferenceData1: this.policyInfo.policyNumber,
          confirmationDisplay: "true",
          saveOption: "Save",
          accountGroupCode: this.accountGroupCode,
          acknowledgmentRequired: "true",
          sessionId: sessionKey
        });
      } else {
        this.$appNotify({
          type: "error",
          title: "Data not loaded",
          message:
            "Please reload page or Call Billing at 888-786-0003, option #2"
        });
        this.isProcessingPayment = false;
      }
    },
    goToPrevious() {
      this.$emit("gotoPrevious");
    },
    formatAmount() {
      if (this.policyInfo && this.policyInfo.currentAmtDue) {
        let amount = parseFloat(this.policyInfo.currentAmtDue).toFixed(2);

        this.editField({ key: "policyInfo.currentAmtDue", value: amount });
      } else {
        let amount = parseFloat("0.00").toFixed(2);
        this.editField({ key: "policyInfo.currentAmtDue", value: amount });
      }
    }
  },
  computed: {
    ...quickpayMapState(["policyInfo", "makingApiRequest"]),
    paymentSuccessMsg(): string {
      return `Payment succeeded. You have to wait 2 mins if you want to make another payment on the policy ${this.policyInfo?.policyPrefix} ${this.policyInfo.policyNumber} and with amount $ ${this.policyInfo.currentAmtDue} by ${this.policyInfo.paymentMethod} method`;
    },
    mode(): string {
      return this.policyInfo &&
        parseFloat(this.policyInfo.totalAmountDue) -
          parseFloat(this.policyInfo.currentAmtDue) ===
          0
        ? "F"
        : "P";
    },
    amountLessThanFive(): boolean {
      if (
        this.policyInfo.companyNumber != "20" &&
        parseFloat(this.policyInfo.currentAmtDue) < 5
      ) {
        return true;
      }
      return false;
    },
    validCurrentAmount(): boolean | null {
      return (
        this.policyInfo &&
        !(
          parseFloat(this.policyInfo.currentAmtDue) <
            parseFloat(this.policyInfo.minAmtDue) ||
          parseFloat(this.policyInfo.currentAmtDue) >
            parseFloat(this.policyInfo.totalAmountDue)
        )
      );
    },
    totalAmountZero(): boolean {
      return this.policyInfo
        ? parseFloat(this.policyInfo.totalAmountDue) === 0 ||
            this.policyInfo.policyStatus === "P"
        : false;
    },
    disableButton(): boolean {
      return (
        this.isInUS &&
        (this.isProcessingPayment ||
          this.totalAmountZero ||
          !this.validCurrentAmount ||
          !this.policyInfo?.paymentMethod ||
          !this.showCaution ||
          !this.policyInfo.currentAmtDue ||
          parseFloat(this.policyInfo.currentAmtDue) === 0)
      );
    },
    currentAmtDue(): number {
      return parseFloat(this.policyInfo.currentAmtDue);
    },
    feeContext() {
      let paymentFee = "Paymentwithoutfee";
      if (this.policyInfo?.paymentMethod == "CreditCard") {
        paymentFee = "PaymentWithFee";
      }
      return paymentFee;
    },
    accountGroupCode(): number | string {
      const { companyNumber, CompanyNum } = this.policyInfo || {};

      const parsedCompanyNumber = parseInt(companyNumber);
      const parsedCompanyNum = parseInt(CompanyNum);

      let accountGroupCode = !isNaN(parsedCompanyNumber)
        ? parsedCompanyNumber
        : !isNaN(parsedCompanyNum)
        ? parsedCompanyNum
        : 0;
      return accountGroupCode;
    },
    getData() {
      let result = {};
      result = {
        sections: [
          {
            sectionTitle: "",
            showDivider: true,
            showSection: true,
            sectionColumnSize: "2",
            information: [
              {
                informationColumnSize: "1",
                key: "Insured Name",
                value: get(this.policyInfo, "name")
              },
              {
                informationColumnSize: "1",
                key: "Policy",
                value: get(this.policyInfo, "policy")
              }
            ]
          },
          {
            sectionTitle: "",
            showDivider: true,
            showSection: true,
            sectionColumnSize: "2",
            information: [
              {
                informationColumnSize: "1",
                key: "Current Due Date",
                value: get(this.policyInfo, "currentDueDate")
                  ? formatDate(get(this.policyInfo, "currentDueDate"))
                  : ""
              },
              {
                informationColumnSize: "1",
                key: "Minimum Amount Due",
                value: formatCurrencyFloat(get(this.policyInfo, "minAmtDue"))
              }
            ]
          },
          {
            sectionTitle: "",
            showDivider: true,
            showSection: true,
            sectionColumnSize: "2",
            information: [
              {
                informationColumnSize: "1",
                key: "Last Payment",
                value: formatCurrencyFloat(get(this.policyInfo, "lastPayment"))
              },
              {
                informationColumnSize: "1",
                key: "Pay In Full",
                value: formatCurrencyFloat(
                  get(this.policyInfo, "totalAmountDue")
                )
              }
            ]
          }
        ]
      };

      return result;
    }
  }
});
